import { useStore } from '@nanostores/react';
import { activeRouteStore } from '@customerio/hydra-nav-state';
import { createContext, use } from 'react';
import { useParams } from 'react-router-dom';
import { DeliverySettings, useListWorkspaces, useSampleWorkspace } from '@/features/workspaces';
import type { MergedWorkspace } from '@/features/workspaces';
import { authStore } from '@/services/auth/store';
import { isEmptyString } from '@/utils/string';

const CurrentWorkspaceContext = createContext<MergedWorkspace | null>(null);

function findWorkspace(workspaces: MergedWorkspace[], id: number) {
  return (
    workspaces.find((workspace) => {
      return workspace.id === id;
    }) ?? null
  );
}

function getDefaultWorkspace(workspaces: MergedWorkspace[], sampleWorkspaceId: number | null = null) {
  if (workspaces.length === 0) {
    return null;
  }

  const sortedWorkspaces = workspaces.filter((w) => w.id !== sampleWorkspaceId).sort((a, b) => a.id - b.id);

  return sortedWorkspaces?.[0] ?? null;
}

// Re-export workspace ID utilities for backward compatibility
export { getWorkspaceIdFromUrl, getWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';

function useCurrentWorkspaceInternal() {
  const workspaces = useListWorkspaces();
  const auth = useStore(authStore);
  const { sampleAccountId, sampleWorkspaceId } = useSampleWorkspace();
  let { workspaceId: workspaceIdParameter } = useParams();

  // Consume activeRouteStore to force updating this hook when the route changes
  useStore(activeRouteStore);

  if (workspaces.isPending && workspaces.data == null) {
    return null;
  }

  if (workspaceIdParameter === 'last') {
    workspaceIdParameter =
      auth.user?.last_cio_workspace_id != null && auth.user?.last_cio_workspace_id !== '0'
        ? auth.user.last_cio_workspace_id
        : undefined;
  }

  // If there is a workspaceId parameter, just use that to get the current
  // workspace
  if (!isEmptyString(workspaceIdParameter)) {
    const workspace = findWorkspace(workspaces.data ?? [], Number.parseInt(workspaceIdParameter));

    if (workspace != null) {
      return workspace;
    } else if (
      String(workspaceIdParameter) === String(sampleWorkspaceId) &&
      sampleWorkspaceId != null &&
      sampleAccountId != null
    ) {
      // For new accounts, sometimes it appears that it takes a while for the
      // sample workspace data to be loaded on the `useListWorkspaces` hook,
      // likely due to permissions caching.
      // In this case, if the workspace id matches the expected sample
      // workspace id, we return a skeleton workspace object with the sample
      // workspace and account ids to prevent issues.
      return {
        id: sampleWorkspaceId,
        account_id: sampleAccountId,
        name: 'Sample Workspace',
        color: '',
        cio_initialized: true,
        cio_workspace_id: sampleWorkspaceId,
        cdp_workspace_id: sampleWorkspaceId,
        cdp_initialized: true,
        user_ids: [],
        user_roles: null,
        delivery_settings: DeliverySettings.Normal,
        delivery_email: '',
      };
    } else {
      // If the workspace doesn't exist, return the default (first) workspace
      return getDefaultWorkspace(workspaces.data ?? [], sampleWorkspaceId);
    }
  }

  let activeWorkspaceId: string | null = null;

  let workspace: MergedWorkspace | null = null;

  // If we still don't have an active workspace id, fall back to last Journeys
  // workspace, if there is one (regardless of which app is "active")
  if (activeWorkspaceId == null) {
    if (auth.user?.last_cio_workspace_id != null && auth.user?.last_cio_workspace_id !== '0') {
      activeWorkspaceId = auth.user.last_cio_workspace_id;
    }
  }

  if (activeWorkspaceId != null && workspace == null) {
    workspace = findWorkspace(workspaces.data ?? [], Number.parseInt(activeWorkspaceId));
  }

  // If we still don't have an active workspace, fall back to the default
  workspace ??= getDefaultWorkspace(workspaces.data ?? [], sampleWorkspaceId);

  return workspace;
}

export function useCurrentWorkspace() {
  return use(CurrentWorkspaceContext);
}

export function CurrentWorkspaceProvider({ children }: { children: React.ReactNode }) {
  const currentWorkspace = useCurrentWorkspaceInternal();

  return <CurrentWorkspaceContext.Provider value={currentWorkspace}>{children}</CurrentWorkspaceContext.Provider>;
}
